import { useEffect, useState } from "react";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { BrandedErrorLayout } from "./BrandedErrorLayout";
import { Sentry } from "@/lib/sentry-init";

interface ServerErrorPageProps {
  error?: unknown;
  resetError?: () => void;
}

/**
 * Brand-styled 500 page. Used as both a route component (/500) and the
 * Sentry.ErrorBoundary fallback. Captures the error to Sentry on mount and
 * surfaces a copyable reference ID for support — never a stack trace.
 */
export function ServerErrorPage({ error, resetError }: ServerErrorPageProps) {
  const [refId, setRefId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!error) return;
    const id = Sentry.captureException(error, {
      tags: { boundary: "server_error_page" },
    });
    if (id) setRefId(id.slice(-8));
  }, [error]);

  const handleRefresh = () => {
    resetError?.();
    if (typeof window !== "undefined") window.location.reload();
  };

  return (
    <BrandedErrorLayout
      eyebrow="Unexpected Error"
      sealTitle="500"
      heading={
        <>
          Something on <em className="italic text-bronze-600">our</em> end.
        </>
      }
      body="We've been notified and are looking into it. While we investigate, try refreshing or returning home."
      footnote={
        <>
          If this keeps happening, reach us at{" "}
          <a
            href="mailto:currencythoughtsllc@gmail.com"
            className="text-navy-800 underline-offset-4 hover:underline"
          >
            currencythoughtsllc@gmail.com
          </a>
          {refId && (
            <>
              <span className="mx-2 text-bronze-600">·</span>
              <span>Reference&nbsp;</span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(refId).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  });
                }}
                className="rounded bg-cream-50 px-2 py-0.5 font-mono text-[12px] text-navy-800 hover:bg-bronze-400/30"
                aria-label={`Copy error reference ${refId}`}
              >
                {copied ? "copied" : refId}
              </button>
            </>
          )}
        </>
      }
    >
      <div className="flex flex-wrap items-center justify-center gap-3">
        <PrimaryButton onClick={handleRefresh}>Refresh page</PrimaryButton>
        <SecondaryButton
          className="border-bronze-600 text-navy-800 hover:bg-bronze-400 hover:text-navy-900"
          onClick={() => {
            if (typeof window !== "undefined") window.location.href = "/";
          }}
        >
          Return home
        </SecondaryButton>
      </div>
    </BrandedErrorLayout>
  );
}

export default ServerErrorPage;